/**
 * §B1.5: `start_xero_authorization` is followed by `window.location.assign(url)`, which **destroys the page**.
 * The three values the connect step cannot rebuild afterwards are written here, read back on
 * `/oauth/xero/return`, and cleared on `create_migration` success or on navigating away.
 *
 * **Nothing secret is ever put here**: no API key, no token, no session value, no CSRF token. The allow-list
 * below is enforced on the way in — `save` drops anything that is not one of the three — and
 * `web/tests/connect.test.tsx` asserts the stored object's keys are a subset of it, which is the test §B1.5
 * asks for by name.
 *
 * The source connection id is deliberately **not** here: at redirect time there is no source connection yet,
 * and after the return there is no page destruction between the tenant selection and the consent step, so that
 * hand-off travels in React Router's `location.state` instead. Widening this allow-list is a decision, not a
 * convenience.
 */

export const DRAFT_KEY = "lucimig.newMigration";

/**
 * `destination_system` (Package W2c): the wizard is no longer hardcoded to `lucibook`, so the system that goes
 * with `destination_connection_id` has to survive the same OAuth round trip the connection id already does —
 * `XeroReturn` reads it back rather than assuming a fixed destination.
 */
export const ALLOWED_DRAFT_KEYS = [
  "destination_connection_id",
  "destination_system",
  "xero_display_name",
  "return_to",
] as const;

export type DraftKey = (typeof ALLOWED_DRAFT_KEYS)[number];

export type NewMigrationDraft = Partial<Record<DraftKey, string>>;

function isAllowed(key: string): key is DraftKey {
  return (ALLOWED_DRAFT_KEYS as readonly string[]).includes(key);
}

/** Only the allow-listed keys, and only non-empty strings. Everything else is dropped silently. */
export function sanitiseDraft(value: unknown): NewMigrationDraft {
  if (typeof value !== "object" || value === null) return {};
  const out: NewMigrationDraft = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (isAllowed(key) && typeof raw === "string" && raw !== "") {
      out[key] = raw;
    }
  }
  return out;
}

export function readDraft(): NewMigrationDraft {
  try {
    const raw = window.sessionStorage.getItem(DRAFT_KEY);
    return raw === null ? {} : sanitiseDraft(JSON.parse(raw));
  } catch {
    // A private window, blocked site data or a corrupt value: an empty draft is a working screen.
    return {};
  }
}

/** Merges into whatever is already stored, then sanitises the whole thing before it is written. */
export function saveDraft(patch: NewMigrationDraft): NewMigrationDraft {
  const next = sanitiseDraft({ ...readDraft(), ...patch });
  try {
    window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
  } catch {
    // Storage is a convenience here, not a dependency: the return screen falls back to the tenant's own name.
  }
  return next;
}

export function clearDraft(): void {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}
